// CSR-857: Contacts section for the Insurance Company detail page.
// Lives below Policies (and Child Companies for parent_company). Uses
// the shared DetailSectionHeader so the visual matches Policies and
// Child Companies. The body is a table (Name / Role / Email / Phone /
// Primary / actions) mirroring the lodestar EntityContacts prototype.
// Add / Edit / Delete go through the CSR-820 endpoints.

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import {
  useDeleteInsuranceCompanyContact,
  useInsuranceCompanyContacts,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { InsuranceCompanyContact } from "@shared/schema";
import { ContactFormDialog } from "./ContactFormDialog";
import { DetailSectionHeader } from "./DetailSectionHeader";

interface ContactRowProps {
  contact: InsuranceCompanyContact;
  onEdit: (contact: InsuranceCompanyContact) => void;
  onDelete: (contact: InsuranceCompanyContact) => void;
}

function ContactRow({ contact, onEdit, onDelete }: ContactRowProps) {
  return (
    <tr
      className="hover:bg-muted/30 transition-colors"
      data-testid={`contact-row-${contact.id}`}
    >
      <td className="px-6 py-4 font-medium">{contact.fullName}</td>
      <td className="px-6 py-4 text-muted-foreground" data-testid="contact-role">
        {contact.role || "-"}
      </td>
      <td className="px-6 py-4 text-muted-foreground">
        {contact.email ? (
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-1.5 hover:text-foreground"
            data-testid="contact-email"
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{contact.email}</span>
          </a>
        ) : (
          "-"
        )}
      </td>
      <td className="px-6 py-4 text-muted-foreground">
        {contact.phone ? (
          <span
            className="flex items-center gap-1.5"
            data-testid="contact-phone"
          >
            <Phone className="w-3.5 h-3.5 shrink-0" />
            {contact.phone}
          </span>
        ) : (
          "-"
        )}
      </td>
      <td className="px-6 py-4">
        {contact.isPrimary ? (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap bg-primary/10 text-primary border-primary/20"
            data-testid="badge-primary"
          >
            Primary
          </span>
        ) : null}
      </td>
      <td className="px-6 py-4 w-20">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(contact)}
            aria-label={`Edit ${contact.fullName}`}
            data-testid={`button-edit-contact-${contact.id}`}
          >
            <Pencil className="w-4 h-4 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(contact)}
            aria-label={`Delete ${contact.fullName}`}
            data-testid={`button-delete-contact-${contact.id}`}
          >
            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive transition-colors" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

interface ContactsSectionProps {
  companyId: string;
}

export function ContactsSection({ companyId }: ContactsSectionProps) {
  const { data, isLoading, error } = useInsuranceCompanyContacts(companyId);
  const deleteMutation = useDeleteInsuranceCompanyContact();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingContact, setEditingContact] =
    useState<InsuranceCompanyContact | null>(null);

  const [deletingContact, setDeletingContact] =
    useState<InsuranceCompanyContact | null>(null);

  const count = data?.length ?? 0;

  const handleAdd = () => {
    setEditingContact(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const handleEdit = (contact: InsuranceCompanyContact) => {
    setEditingContact(contact);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingContact) return;
    const target = deletingContact;
    deleteMutation.mutate(
      { id: target.id, companyId },
      {
        onSuccess: () => {
          toast({ title: "Contact removed" });
          setDeletingContact(null);
        },
        onError: (err) => {
          toast({
            title: "Failed to remove contact",
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          });
          setDeletingContact(null);
        },
      },
    );
  };

  const showTable = !error && !isLoading && data && data.length > 0;

  return (
    <section className="flex flex-col gap-3" data-testid="section-contacts">
      <Card className="overflow-hidden p-0">
        <DetailSectionHeader
          icon={<Users className="w-5 h-5 text-primary" />}
          title="Contacts"
          count={count > 0 ? count : undefined}
          testid="contacts-total"
          action={
            <Button
              size="sm"
              onClick={handleAdd}
              data-testid="button-add-contact"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Contact
            </Button>
          }
        />
        {error ? (
          <div
            className="p-6 text-sm text-destructive"
            data-testid="contacts-error"
          >
            Failed to load contacts.
          </div>
        ) : isLoading || !data ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading contacts…
          </div>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 font-semibold">Name</th>
                <th className="px-6 py-3 font-semibold">Role</th>
                <th className="px-6 py-3 font-semibold">Email</th>
                <th className="px-6 py-3 font-semibold">Phone</th>
                <th className="px-6 py-3 font-semibold">Primary</th>
                <th className="px-6 py-3 font-semibold w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {showTable ? (
                data.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onEdit={handleEdit}
                    onDelete={setDeletingContact}
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-muted-foreground"
                    data-testid="contacts-empty"
                  >
                    No contacts added yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      <ContactFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        companyId={companyId}
        contact={editingContact}
      />

      <AlertDialog
        open={!!deletingContact}
        onOpenChange={(open) => !open && setDeletingContact(null)}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-contact">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove contact</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingContact
                ? `Are you sure you want to remove "${deletingContact.fullName}"?`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-contact"
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
